/**
 * MaterialsSection Component
 * 
 * Displays materials for sessions with version awareness and download tracking.
 * Integrates with the CDN-first materials management system.
 */

import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Download, 
  ChevronUp, 
  ChevronDown, 
  ExternalLink,
  Users,
  AlertCircle,
  X
} from 'lucide-react';
import { Spinner } from './ui/spinner';
import { useVersionFiles, useMaterialAccess } from '../hooks/useMaterialsVersioning';
import * as Sentry from '@sentry/react';

// Simple relative time utility (replace date-fns dependency)
const formatTimeAgo = (date) => {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'yesterday';
  return `${diffDays} days ago`;
};

/**
 * Individual file display component with download tracking
 */
const MaterialFile = ({ file, soContext, onAccessLog }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (e) => {
    e.preventDefault();
    setDownloading(true);

    try {
      // Log the access
      if (onAccessLog) {
        await onAccessLog(file.id, 'Direct Download', soContext);
      }

      // Open download link
      window.open(file.cdn_url, '_blank');
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'MaterialFile', operation: 'download' },
        extra: { fileId: file.id, soContext }
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleShortLinkCopy = async () => {
    try {
      await navigator.clipboard.writeText(file.short_link_url);
      
      // Log the access
      if (onAccessLog) {
        await onAccessLog(file.id, 'Short Link Copy', soContext);
      }
    } catch (error) {
      console.error('Failed to copy short link:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="flex items-center justify-between p-3 border rounded-md bg-muted/50 hover:bg-muted/70 transition-colors">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm font-medium truncate">{file.display_name || file.original_filename}</p>
            {file.download_count > 0 && (
              <Badge variant="secondary" className="text-xs">
                {file.download_count} downloads
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{formatFileSize(file.file_size_bytes)}</span>
            {file.content_type && (
              <span>• {file.content_type.split('/')[1]?.toUpperCase()}</span>
            )}
            {file.uploaded_at && (
              <span>• Added {formatTimeAgo(file.uploaded_at)}</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {file.short_link_url && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShortLinkCopy}
            className="h-8 w-8 p-0"
            title="Copy short link"
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownload}
          disabled={downloading}
          className="gap-1"
        >
          {downloading ? (
            <Spinner size="small" />
          ) : (
            <Download className="h-3 w-3" />
          )}
          <span className="hidden sm:inline">Download</span>
        </Button>
      </div>
    </div>
  );
};

/**
 * Version info display component (available for future use)
 */
// const VersionInfo = ({ version, isLocked = false }) => {
//   if (!version) return null;
//   return (
//     <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
//       <Clock className="h-4 w-4" />
//       <span>
//         Version {version.version_id}
//         {isLocked && (
//           <Badge variant="secondary" className="ml-2 text-xs">
//             Locked
//           </Badge>
//         )}
//       </span>
//       {version.created_at && (
//         <span>
//           • Updated {formatTimeAgo(version.created_at)}
//         </span>
//       )}
//       {version.materials_count > 0 && (
//         <span>• {version.materials_count} files</span>
//       )}
//     </div>
//   );
// };

/**
 * Session materials component
 */
const SessionMaterials = ({ session, soId, versionId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { files, loading, error } = useVersionFiles(versionId);
  const { logAccess } = useMaterialAccess();

  const handleAccessLog = useCallback(async (fileId, accessMethod, soContext) => {
    try {
      await logAccess(fileId, accessMethod, soContext);
    } catch (error) {
      // Don't throw - logging shouldn't break user experience
      console.warn('Failed to log material access:', error);
    }
  }, [logAccess]);

  if (!versionId) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 border rounded-md bg-muted/30">
        <AlertCircle className="h-4 w-4" />
        <span>No materials available for this session</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600 p-3 border border-red-200 rounded-md bg-red-50">
        <AlertCircle className="h-4 w-4" />
        <span>Error loading materials: {error}</span>
      </div>
    );
  }

  const hasFiles = files && files.length > 0;

  return (
    <div className="border rounded-md">
      <Button
        variant="ghost"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full justify-between p-3 h-auto"
      >
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          <span className="font-medium">
            Session {session.sessionNo} Materials
          </span>
          {hasFiles && (
            <Badge variant="outline" className="text-xs">
              {files.length} files
            </Badge>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </Button>

      {isExpanded && (
        <div className="border-t p-3 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Spinner size="default" className="mr-2" />
              <span className="text-muted-foreground">Loading materials...</span>
            </div>
          ) : hasFiles ? (
            <div className="space-y-2">
              {files.map(file => (
                <MaterialFile
                  key={file.id}
                  file={file}
                  soContext={soId}
                  onAccessLog={handleAccessLog}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <AlertCircle className="h-4 w-4" />
              <span>No materials available for this session</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Main Materials Section component
 */
const MaterialsSection = ({ 
  programData, 
  loading = false, 
  showInstructions = false,
  onToggleInstructions 
}) => {
  if (!programData || !programData.sessions) {
    return null;
  }

  /**
   * Check if any session has materials available
   * @returns {boolean} True if at least one session has materials version data
   */
  const hasMaterials = programData.sessions.length > 0 && programData.sessions.some(session => 
    session.so_materials_version || session.master_materials_version
  );

  // Debug logging for development troubleshooting
  if (process.env.NODE_ENV === 'development') {
    console.log('MaterialsSection: hasMaterials =', hasMaterials, 
                'sessions =', programData.sessions.length,
                'loading =', loading);
  }

  // Hide the entire materials section if no materials are available and not loading
  if (!loading && !hasMaterials) {
    return null;
  }

  return (
    <Card className="bg-card shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="border-b px-4 py-4 sm:px-6 flex flex-row items-center justify-between">
        <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Materials
        </CardTitle>
        {!loading && !showInstructions && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleInstructions}
            className="gap-2"
          >
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Help</span>
          </Button>
        )}
      </CardHeader>

      <CardContent className="p-4 sm:p-6">
        {/* Instructions */}
        {!loading && showInstructions && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex justify-between items-start">
              <div className="text-xs sm:text-sm text-blue-800 space-y-2 flex-1">
                <div className="font-semibold">Materials Access:</div>
                <div className="space-y-1">
                  <div>• Click on each session to view and download available materials</div>
                  <div>• Materials are automatically tracked for analytics and usage reporting</div>
                  <div>• Short links can be copied for easy sharing with attendees</div>
                  <div>• Each SO uses a specific version of materials (locked for consistency)</div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleInstructions}
                className="ml-2 h-6 w-6 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner size="default" className="mr-2" />
            <span className="text-muted-foreground">Loading materials...</span>
          </div>
        ) : (
          <div className="space-y-3">
            {programData.sessions.map((session) => {
              // Get the version ID for this session/SO combination
              // This would come from the session data once integrated with the version system
              const versionId = session.so_materials_version || session.master_materials_version;
              
              return (
                <SessionMaterials
                  key={session.sessionId}
                  session={session}
                  soId={programData.so}
                  versionId={versionId}
                />
              );
            })}

            {programData.sessions.length === 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4 justify-center">
                <AlertCircle className="h-4 w-4" />
                <span>No sessions found for this program</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MaterialsSection;